import { useEffect, useMemo, useRef, useState } from 'react';
import api from '../lib/api';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend, PieChart, Pie, Cell } from 'recharts';
import { Activity, IndianRupee, Star, ClipboardCheck, Calendar, TrendingUp, Users, UserMinus, UserPlus } from 'lucide-react';

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const Card = ({ children, className = '', ...rest }) => (
  <div className={`bg-white border border-slate-200 rounded-2xl shadow-sm ${className}`} {...rest}>{children}</div>
);

/**
 * Defers Recharts render until parent has a measured non-zero width.
 * Avoids ResponsiveContainer's first-paint width(-1) warning.
 */
function FlexChart({ height = 240, children }) {
  const ref = useRef(null);
  const [w, setW] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => { const cw = el.clientWidth || 0; if (cw > 0) setW(cw); };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <div ref={ref} className="w-full min-w-0" style={{ height }}>
      {w > 0 && children({ width: w, height })}
    </div>
  );
}

const PIE_COLORS = ['#3D4A8E', '#7C3AED', '#10B981', '#F59E0B', '#EF4444', '#0EA5E9', '#EC4899'];

export default function Analytics() {
  const [data, setData] = useState(null);
  const [hc, setHc] = useState(null);
  const [months, setMonths] = useState(6);

  useEffect(() => {
    (async () => {
      try {
        const [s, h] = await Promise.all([
          api.get(`/analytics/summary?months=${months}`),
          api.get(`/analytics/headcount?months=${months}`),
        ]);
        setData(s.data);
        setHc(h.data);
      } catch (e) { console.error(e); }
    })();
  }, [months]);

  const leaveData = useMemo(() => (data?.leaves?.by_month || []).map(b => ({
    month: b.month.slice(5), paid: b.paid, sick: b.sick, unpaid: b.unpaid
  })), [data]);

  const headcountData = useMemo(() => (hc?.series || []).map(b => ({
    month: b.month.slice(5),
    headcount: b.headcount,
    joiners: b.joiners,
    leavers: b.leavers,
    attrition: b.attrition_pct,
  })), [hc]);

  const reimbMonthData = useMemo(() => (data?.reimbursements?.by_month || []).map(b => ({
    month: b.month.slice(5), total: b.total
  })), [data]);

  const reimbCatData = useMemo(() => data?.reimbursements?.by_category || [], [data]);

  const ratingDist = useMemo(() => data?.reviews?.rating_distribution || [], [data]);

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Insights</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mt-1">HR Analytics</h1>
          <p className="text-slate-500 mt-1">Spot trends across leaves, reimbursements and performance reviews.</p>
        </div>
        <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-full px-3 py-1.5 shadow-sm" data-testid="analytics-window">
          <Calendar className="w-4 h-4 text-slate-500" strokeWidth={1.8} />
          <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Window</span>
          {[3, 6, 12].map(m => (
            <button key={m} onClick={() => setMonths(m)} data-testid={`analytics-window-${m}`}
                    className={`px-2.5 py-1 text-xs font-semibold rounded-full transition ${months === m ? 'bg-[#3D4A8E] text-white' : 'text-slate-600 hover:bg-slate-100'}`}>
              {m}M
            </button>
          ))}
        </div>
      </div>

      {!data && (
        <Card className="p-12 text-center text-slate-500" data-testid="analytics-loading">
          Loading analytics…
        </Card>
      )}

      {data && (
        <>
          {/* ---- Top KPI strip ---- */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <Card className="p-6" data-testid="kpi-leaves">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Leaves taken</div>
                  <div className="font-heading text-4xl font-bold mt-2 text-slate-900">{data.leaves.grand_total}</div>
                  <div className="text-xs text-slate-500 mt-1">days · last {months} months</div>
                </div>
                <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
                  <Activity className="w-5 h-5" strokeWidth={1.8} />
                </div>
              </div>
            </Card>
            <Card className="p-6" data-testid="kpi-reimb">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Reimb. paid</div>
                  <div className="font-heading text-4xl font-bold mt-2 text-slate-900">{inr(data.reimbursements.grand_total)}</div>
                  <div className="text-xs text-slate-500 mt-1">credited · last {months} months</div>
                </div>
                <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
                  <IndianRupee className="w-5 h-5" strokeWidth={1.8} />
                </div>
              </div>
            </Card>
            <Card className="p-6" data-testid="kpi-reviews">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Avg review score</div>
                  <div className="font-heading text-4xl font-bold mt-2 text-slate-900">
                    {data.reviews?.avg_overall != null ? data.reviews.avg_overall : '—'}
                    {data.reviews?.avg_overall != null && <span className="text-base font-normal text-slate-400 ml-1">/ 5</span>}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    {data.reviews?.cycle_name ? `${data.reviews.cycle_name} · ${data.reviews.submitted}/${data.reviews.total} submitted` : 'No active cycle'}
                  </div>
                </div>
                <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center">
                  <Star className="w-5 h-5" strokeWidth={1.8} />
                </div>
              </div>
            </Card>
          </div>

          {/* ---- Headcount KPI mini-strip ---- */}
          {hc && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5" data-testid="headcount-kpi-strip">
              <Card className="p-5" data-testid="kpi-active-headcount">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Active headcount</div>
                    <div className="font-heading text-3xl font-bold mt-2 text-slate-900">{hc.current.active}</div>
                    <div className="text-xs text-slate-500 mt-1">{hc.current.terminated} offboarded</div>
                  </div>
                  <div className="w-9 h-9 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center">
                    <Users className="w-4 h-4" strokeWidth={1.8} />
                  </div>
                </div>
              </Card>
              <Card className="p-5" data-testid="kpi-joiners">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Joiners</div>
                    <div className="font-heading text-3xl font-bold mt-2 text-emerald-700">{hc.totals.joiners}</div>
                    <div className="text-xs text-slate-500 mt-1">last {months} months</div>
                  </div>
                  <div className="w-9 h-9 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
                    <UserPlus className="w-4 h-4" strokeWidth={1.8} />
                  </div>
                </div>
              </Card>
              <Card className="p-5" data-testid="kpi-leavers">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Leavers</div>
                    <div className="font-heading text-3xl font-bold mt-2 text-rose-700">{hc.totals.leavers}</div>
                    <div className="text-xs text-slate-500 mt-1">last {months} months</div>
                  </div>
                  <div className="w-9 h-9 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center">
                    <UserMinus className="w-4 h-4" strokeWidth={1.8} />
                  </div>
                </div>
              </Card>
              <Card className="p-5" data-testid="kpi-attrition">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Attrition</div>
                    <div className="font-heading text-3xl font-bold mt-2 text-slate-900">{hc.totals.annualized_attrition_pct}%</div>
                    <div className="text-xs text-slate-500 mt-1">avg HC {hc.totals.avg_headcount}</div>
                  </div>
                  <div className="w-9 h-9 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center">
                    <TrendingUp className="w-4 h-4" strokeWidth={1.8} />
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* ---- Headcount trend + Attrition chart ---- */}
          {hc && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="headcount-charts">
              <Card className="p-6" data-testid="card-headcount-trend">
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Headcount trend</div>
                <h3 className="font-heading text-xl font-semibold text-slate-900 mt-1 mb-3">Active employees over time</h3>
                <FlexChart height={260}>
                  {({ width, height }) => (
                    <LineChart width={width} height={height} data={headcountData} margin={{ top: 5, right: 15, left: -5, bottom: 5 }}>
                      <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" />
                      <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#64748B' }} />
                      <YAxis tick={{ fontSize: 11, fill: '#64748B' }} />
                      <Tooltip />
                      <Line type="monotone" dataKey="headcount" stroke="#3D4A8E" strokeWidth={2.5} dot={{ r: 3 }} name="Headcount" />
                    </LineChart>
                  )}
                </FlexChart>
              </Card>
              <Card className="p-6" data-testid="card-attrition">
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Joiners vs leavers</div>
                <h3 className="font-heading text-xl font-semibold text-slate-900 mt-1 mb-3">Monthly attrition flow</h3>
                <FlexChart height={260}>
                  {({ width, height }) => (
                    <BarChart width={width} height={height} data={headcountData} margin={{ top: 5, right: 15, left: -5, bottom: 5 }}>
                      <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" />
                      <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#64748B' }} />
                      <YAxis tick={{ fontSize: 11, fill: '#64748B' }} />
                      <Tooltip />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="joiners" fill="#10B981" name="Joiners" />
                      <Bar dataKey="leavers" fill="#EF4444" name="Leavers" />
                    </BarChart>
                  )}
                </FlexChart>
              </Card>
            </div>
          )}

          {/* ---- Leave trends ---- */}
          <Card className="p-6" data-testid="card-leave-trends">
            <div className="flex items-center justify-between mb-1">
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Leave usage</div>
                <h2 className="font-heading text-xl font-semibold text-slate-900 mt-1">Monthly leave trend</h2>
              </div>
              <div className="flex items-center gap-3 text-xs text-slate-500">
                <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-[#3D4A8E]" /> Paid {data.leaves.totals_by_type.paid}</span>
                <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-rose-500" /> Sick {data.leaves.totals_by_type.sick}</span>
                <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-slate-400" /> Unpaid {data.leaves.totals_by_type.unpaid}</span>
              </div>
            </div>
            <FlexChart height={260}>
              {({ width, height }) => (
                <BarChart width={width} height={height} data={leaveData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="month" stroke="#94A3B8" fontSize={11} />
                  <YAxis stroke="#94A3B8" fontSize={11} allowDecimals={false} />
                  <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #E2E8F0', fontSize: 12 }} />
                  <Bar dataKey="paid" stackId="a" fill="#3D4A8E" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="sick" stackId="a" fill="#F43F5E" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="unpaid" stackId="a" fill="#94A3B8" radius={[4, 4, 0, 0]} />
                </BarChart>
              )}
            </FlexChart>
          </Card>

          {/* ---- Reimbursements (line + pie) ---- */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
            <Card className="lg:col-span-3 p-6" data-testid="card-reimb-trend">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Spend</div>
              <h2 className="font-heading text-xl font-semibold text-slate-900 mt-1 mb-3">Reimbursements paid over time</h2>
              <FlexChart height={250}>
                {({ width, height }) => (
                  <LineChart width={width} height={height} data={reimbMonthData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="month" stroke="#94A3B8" fontSize={11} />
                    <YAxis stroke="#94A3B8" fontSize={11} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v) => inr(v)}
                             contentStyle={{ borderRadius: 12, border: '1px solid #E2E8F0', fontSize: 12 }} />
                    <Line type="monotone" dataKey="total" stroke="#10B981" strokeWidth={2.5} dot={{ r: 4, fill: '#10B981' }} />
                  </LineChart>
                )}
              </FlexChart>
            </Card>
            <Card className="lg:col-span-2 p-6" data-testid="card-reimb-by-category">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Breakdown</div>
              <h2 className="font-heading text-xl font-semibold text-slate-900 mt-1 mb-3">Spend by category</h2>
              {reimbCatData.length === 0 ? (
                <div className="text-sm text-slate-500 py-12 text-center">No reimbursements paid in this window.</div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <FlexChart height={180}>
                    {({ width, height }) => (
                      <PieChart width={width} height={height}>
                        <Pie data={reimbCatData} dataKey="total" nameKey="category" cx="50%" cy="50%" outerRadius={70} innerRadius={36}
                             stroke="#fff" strokeWidth={2}>
                          {reimbCatData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                        </Pie>
                        <Tooltip formatter={(v) => inr(v)} />
                      </PieChart>
                    )}
                  </FlexChart>
                  <div className="space-y-2 text-xs">
                    {reimbCatData.map((c, i) => (
                      <div key={c.category} className="flex items-center justify-between gap-2" data-testid={`reimb-cat-${c.category}`}>
                        <span className="inline-flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-full" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                          <span className="font-medium text-slate-700 capitalize">{c.category}</span>
                        </span>
                        <span className="font-semibold text-slate-900">{inr(c.total)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          </div>

          {/* ---- Reviews ---- */}
          <Card className="p-6" data-testid="card-review-summary">
            <div className="flex items-end justify-between flex-wrap gap-4">
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Performance</div>
                <h2 className="font-heading text-xl font-semibold text-slate-900 mt-1">
                  {data.reviews?.cycle_name || 'Review summary'}
                </h2>
                {data.reviews?.due_date && (
                  <p className="text-xs text-slate-500 mt-0.5">Due {data.reviews.due_date}</p>
                )}
              </div>
              {data.reviews && (
                <div className="flex items-center gap-3 text-xs">
                  <span className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-800 border border-emerald-200 px-3 py-1.5 rounded-full font-semibold">
                    <ClipboardCheck className="w-3.5 h-3.5" strokeWidth={2} /> {data.reviews.submitted} submitted
                  </span>
                  <span className="inline-flex items-center gap-1.5 bg-amber-50 text-amber-800 border border-amber-200 px-3 py-1.5 rounded-full font-semibold">
                    <TrendingUp className="w-3.5 h-3.5" strokeWidth={2} /> {data.reviews.pending} pending
                  </span>
                </div>
              )}
            </div>

            {!data.reviews ? (
              <div className="text-sm text-slate-500 py-8 text-center">No review cycles created yet.</div>
            ) : (
              <FlexChart height={220}>
                {({ width, height }) => (
                  <BarChart width={width} height={height} data={ratingDist}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="rating" stroke="#94A3B8" fontSize={11} tickFormatter={(r) => `${r}★`} />
                    <YAxis stroke="#94A3B8" fontSize={11} allowDecimals={false} />
                    <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #E2E8F0', fontSize: 12 }} />
                    <Bar dataKey="count" fill="#3D4A8E" radius={[4, 4, 0, 0]} />
                  </BarChart>
                )}
              </FlexChart>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
